import { Outlet, useLocation } from 'react-router'
import { TabLink } from '@/components/commons/tab-link'
import { Switch } from '@/components/ui/switch'
import { useAuthStore } from '@/features/auth/store/store'
import { useGetQueueCounts } from '@/features/luxe-submissions/queries/use-get-queue-counts'
import { OPS_ACCESS_USER_ROLES } from '@/features/luxe-submissions/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { VisualVettingSearchParamsSchema } from '@/pages/luxe-submissions-queue-visual-vetting'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsQueueLayout() {
	const { t } = useTranslation()
	const location = useLocation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		VisualVettingSearchParamsSchema
	)
	const user = useAuthStore((state) => state.user)
	const { data: queueCounts } = useGetQueueCounts({
		params: {
			statuses:
				searchParams?.showRejected === 'true'
					? ['rejected']
					: undefined,
			precheckState:
				searchParams.showOnlyToReview === 'true'
					? 'pending_review'
					: undefined
		}
	})
	const isQuoteGenerationRoute = location.pathname.includes(
		routes.LUXE_SUBMISSIONS_QUOTE_GENERATION.handle.fullPath
	)
	const isFinalReviewRoute = location.pathname.includes(
		routes.LUXE_SUBMISSIONS_FINAL_REVIEW.handle.fullPath
	)
	const isLuxeVetter = user?.role === 'luxe_vetter'
	const isLuxeOps = user?.role === 'luxe_ops'
	const showTabs = user?.role
		? OPS_ACCESS_USER_ROLES.includes(user.role)
		: false
	const showAssignedToMeFilter = isLuxeVetter || isFinalReviewRoute

	const handleAssignedToMeChange = (checked: boolean) => {
		setSearchParams({
			assignedToMe: checked ? 'true' : undefined,
			page: undefined
		})
	}
	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			{/* Header */}
			<div className='flex items-center justify-between border-neutral-200 border-b px-10 pt-6 pb-6'>
				{isQuoteGenerationRoute ? (
					<h1 className='font-medium text-lg text-neutral-900'>
						{t(
							'luxe_submissions:listings_approved_for_walkthrough'
						)}
					</h1>
				) : (
					<div className='flex items-center gap-2'>
						<h1 className='font-medium text-lg text-neutral-900'>
							{t('luxe_submissions:luxe_evaluation')}
						</h1>
						<span className='text-base text-neutral-700'>
							{t('luxe_submissions:listings_under_review')}
						</span>
					</div>
				)}

				{showAssignedToMeFilter && (
					<div className='flex items-center gap-2'>
						<span className='text-neutral-700 text-sm'>
							{t('luxe_submissions:assigned_to_me')}
						</span>
						<Switch
							checked={searchParams?.assignedToMe === 'true'}
							onChange={handleAssignedToMeChange}
						/>
					</div>
				)}
			</div>

			{/* Tabs */}
			{showTabs && (
				<div className='flex items-center gap-1 border-neutral-300 border-b px-10 pt-6'>
					<TabLink
						count={queueCounts?.precheck}
						label={t('luxe_submissions:pre_check')}
						to={
							routes.LUXE_SUBMISSIONS_INITIAL_EVALUATIONS.handle
								.fullPath
						}
					/>
					<TabLink
						count={queueCounts?.visualVetting}
						label={t('luxe_submissions:visual_vetting')}
						to={
							routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING.handle
								.fullPath
						}
					/>
					<TabLink
						count={queueCounts?.finalReview}
						label={t('luxe_submissions:final_review')}
						to={
							routes.LUXE_SUBMISSIONS_FINAL_REVIEW.handle.fullPath
						}
					/>
					{!isLuxeOps && (
						<TabLink
							count={queueCounts?.quoteGeneration}
							label={t('luxe_submissions:quote_generation')}
							to={
								routes.LUXE_SUBMISSIONS_QUOTE_GENERATION.handle
									.fullPath
							}
						/>
					)}
				</div>
			)}

			{/* Content */}
			<div className='flex flex-1 flex-col overflow-hidden'>
				<Outlet />
			</div>
		</div>
	)
}

export { LuxeSubmissionsQueueLayout }
